import { useCallback, useEffect, useState } from "react";

import { API_TIMESHEETS_UTILIZATION_DASHBOARD } from "../../../../../config/Endpoints.js";
import { postApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import type {
  UtilizationFilters,
  UtilizationReportData,
  UtilizationRow,
  UtilizationSummary,
} from "../types.ts";

const EMPTY_SUMMARY: UtilizationSummary = {
  below50: 0,
  upto80: 0,
  upto100: 0,
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function asNumber(value: unknown, fallback = 0): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function parseRow(value: unknown): UtilizationRow | null {
  const row = asRecord(value);
  if (row.employeeId == null || typeof row.employeeName !== "string") return null;
  return {
    employeeId: asNumber(row.employeeId),
    employeeName: row.employeeName,
    department: typeof row.department === "string" ? row.department : "-",
    photoPath:
      typeof row.photoPath === "string" && row.photoPath.trim()
        ? row.photoPath.trim()
        : undefined,
    allocatedHours: asNumber(row.allocatedHours),
    remainingHours: asNumber(row.remainingHours),
  };
}

function parseResponse(payload: unknown): UtilizationReportData {
  const outer = asRecord(payload);
  const source =
    outer.summary == null && outer.data && !Array.isArray(outer.data)
      ? asRecord(outer.data)
      : outer;
  const summary = asRecord(source.summary);
  const rows = Array.isArray(source.data)
    ? source.data.flatMap((item) => {
        const parsed = parseRow(item);
        return parsed ? [parsed] : [];
      })
    : [];

  return {
    summary: {
      below50: asNumber(summary.below50),
      upto80: asNumber(summary.upto80),
      upto100: asNumber(summary.upto100),
    },
    rows,
    totalElements: asNumber(source.totalElements, rows.length),
    totalPages: asNumber(source.totalPages, 1),
    currentPage: asNumber(source.currentPage),
    pageSize: asNumber(source.pageSize, 10),
  };
}

export function useUtilizationReport(
  filters: UtilizationFilters,
  page: number,
  pageSize: number,
) {
  const [report, setReport] = useState<UtilizationReportData>({
    summary: EMPTY_SUMMARY,
    rows: [],
    totalElements: 0,
    totalPages: 1,
    currentPage: 0,
    pageSize,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const { data } = await postApi<unknown>(
        API_TIMESHEETS_UTILIZATION_DASHBOARD,
        accessToken(),
        {
          month: now.getMonth() + 1,
          year: now.getFullYear(),
          employeeId: filters.employeeId,
          departmentId: filters.departmentId,
          category: filters.category,
          page: page - 1,
          size: pageSize,
        },
      );
      if (!signal?.aborted) setReport(parseResponse(data));
    } catch (cause) {
      if (signal?.aborted) return;
      setReport((current) => ({
        ...current,
        rows: [],
        totalElements: 0,
      }));
      setError(cause instanceof Error ? cause.message : "Failed to load utilization report.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    const controller = new AbortController();
    void fetchReport(controller.signal);
    return () => controller.abort();
  }, [fetchReport]);

  return { ...report, loading, error, refetch: fetchReport };
}
